import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import { Button } from './Button'
import { IconTile } from './IconTile'
import { cx } from './cx'
import s from './Toast.module.css'

export type ToastTone = 'good' | 'warn' | 'critical' | 'brand' | 'neutral'

/** One of a toast's actions: secondary ("Not now") then primary ("Start tour"). */
export interface ToastAction {
  label: string
  onAction: () => void
  /** `primary` is the one the toast exists to offer. Default `ghost`. */
  variant?: 'primary' | 'ghost'
}

export interface ToastOptions {
  title: string
  description?: string
  tone?: ToastTone
  /**
   * #2502 §5.7: an ACTION-CAPABLE toast. A toast that asks something stays
   * until it is answered — it never auto-dismisses and a click on its body
   * does not close it, because a question that times out was never asked.
   * Each action runs and then closes the toast.
   */
  actions?: readonly ToastAction[]
  /**
   * A glyph drawn in a 30px brand tile in place of the tone mark — the
   * first-visit toast's compass (the design's `.gd-toast .gi2`).
   */
  icon?: ReactNode
  /**
   * `end` (default) is the feedback corner, bottom-right. `start` is the
   * GUIDANCE corner, bottom-left beside the rail: the first-visit toast sits
   * there so it never covers the drawer the tour is about to open.
   */
  placement?: 'end' | 'start'
}

interface ToastItem extends ToastOptions { id: number; tone: ToastTone }

export interface ToastApi {
  /** Shows a toast and returns its id, so a host can withdraw it. */
  show: (t: ToastOptions) => number
  success: (title: string, description?: string) => void
  error: (title: string, description?: string) => void
  info: (title: string, description?: string) => void
  /** Withdraws a toast — a first-visit offer the person answered elsewhere. */
  dismiss: (id: number) => void
}

const Ctx = createContext<ToastApi | null>(null)

/** The app's feedback channel — a success/error/info toast after an action. One
 *  provider at the root; `useToast()` anywhere below. Auto-dismisses; click to close.
 *  The standard way every surface confirms a mutation (or surfaces its failure). */
export function useToast(): ToastApi {
  const c = useContext(Ctx)
  if (!c) throw new Error('useToast must be used within <ToastProvider>')
  return c
}

export function ToastProvider({ children, duration = 4000 }: { children: ReactNode; duration?: number }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const idRef = useRef(0)
  const remove = useCallback((id: number) => setItems((x) => x.filter((t) => t.id !== id)), [])
  const show = useCallback((t: ToastOptions) => {
    const id = ++idRef.current
    setItems((x) => [...x, { id, ...t, tone: t.tone ?? 'neutral' }])
    if (!t.actions?.length) setTimeout(() => remove(id), duration)
    return id
  }, [duration, remove])

  const api = useMemo<ToastApi>(() => ({
    show,
    success: (title, description) => { show({ title, description, tone: 'good' }) },
    error: (title, description) => { show({ title, description, tone: 'critical' }) },
    info: (title, description) => { show({ title, description, tone: 'brand' }) },
    dismiss: remove,
  }), [show, remove])

  const render = (t: ToastItem) => {
    const asks = (t.actions?.length ?? 0) > 0
    return (
      <div key={t.id} className={cx(s.toast, s[t.tone], asks && s.asks)} role="status" data-testid="toast"
        data-tone={t.tone} data-asks={asks || undefined} onClick={asks ? undefined : () => remove(t.id)}>
        {t.icon != null
          ? <IconTile size="lg" tone="brand" className={s.icon} aria-hidden="true">{t.icon}</IconTile>
          : <span className={s.mark} aria-hidden="true">{t.tone === 'good' ? '✓' : t.tone === 'critical' ? '!' : '•'}</span>}
        <span className={s.body}>
          <span className={s.title}>{t.title}</span>
          {t.description && <span className={s.desc}>{t.description}</span>}
        </span>
        {asks && (
          <span className={s.actions}>
            {t.actions!.map((action) => (
              <Button key={action.label} size="sm" variant={action.variant ?? 'ghost'}
                onClick={() => { remove(t.id); action.onAction() }}>
                {action.label}
              </Button>
            ))}
          </span>
        )}
      </div>
    )
  }

  const end = items.filter((t) => t.placement !== 'start')
  const start = items.filter((t) => t.placement === 'start')
  return (
    <Ctx.Provider value={api}>
      {children}
      <div className={s.viewport} data-testid="toast-viewport" aria-live="polite">
        {end.map(render)}
      </div>
      {start.length > 0 && (
        <div className={cx(s.viewport, s.start)} data-testid="toast-viewport-start" aria-live="polite">
          {start.map(render)}
        </div>
      )}
    </Ctx.Provider>
  )
}
